import React, { useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { css } from '@emotion/core'
import { Trans, useTranslation } from 'react-i18next'
import { FrontTabletLayout } from '@/components/layouts/front-tablet-layout'
import { useSelfCheckOutState } from '@/hooks/use-self-check-out-state'
import { Body } from '@/components/molecules/front-tablet/body'
import { Header } from '@/components/molecules/front-tablet/header'
import { ReadRoomKeyAnnounceModal } from '@/components/organisms/self-checkout/read-room-key-announce-modal'
import * as api from '@/apis/aipass'
import { CardReaderCommand, SelfCheckinCardReaderType } from '@/models/self-checkin/card-reader'
import { selfCardPCIDStorageKey, serialNumberStorageKey } from '@/constants/self-checkin-device-storage'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { SearchCheckOutReservation } from '@/models/reservation'
import { LoaderContextCreator } from '@/contexts/loader'
import { useSelfCheckoutInitialization } from '@/hooks/use-self-checkout-initialization'
import { SearchItem } from '@/models/search-setting'

export const ReadRoomKey: React.FC<{}> = () => {
  const history = useHistory()
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { state, saveState } = useSelfCheckOutState()

  const [processId, setProcessId] = useState<string>()
  const [isShowNotFoundErrorDialog, setIsShowNotFoundErrorDialog] = useState(false)
  const [isShowReadErrorDialog, setIsShowReadErrorDialog] = useState(false)

  const serialNumber = localStorage.getItem(serialNumberStorageKey) as string
  const pcId = localStorage.getItem(selfCardPCIDStorageKey) as string

  const sendReadRequest = async () => {
    try {
      const { processId } = await api.postSelfCheckinCardReader(state.hotelId, serialNumber, pcId, CardReaderCommand.CardReadRequest)
      setProcessId(processId)
    } catch (e) {
      errorHandler(e)
    }
  }

  const { isInitialized, initializedState } = useSelfCheckoutInitialization({
    onInitialized: sendReadRequest,
  })

  const searchReservationByReader = async (readMessage: SelfCheckinCardReaderType, processId: string) => {
    let stayReservations: SearchCheckOutReservation[] = []
    if (!readMessage.checkin?.room_number || !readMessage.checkin?.checkout_date) {
      setIsShowReadErrorDialog(true)
      return
    }
    try {
      setIsLoading(true)
      const res = await api.fetchSelfCheckOutReservations({
        hotelId: state.hotelId,
        roomNumber: `${readMessage.checkin.room_number}`,
        checkoutDate: readMessage.checkin.checkout_date.substring(0, 10),
        processId,
      })
      stayReservations = res.stayReservations
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
    }
    if (stayReservations.length === 0) {
      setIsShowNotFoundErrorDialog(true)
      return
    }
    saveState({
      ...state,
      searchReservation: {
        condition: {
          mode: SearchItem.RoomKey,
          roomNumber: `${readMessage.checkin.room_number}`,
        },
        result: { reservations: stayReservations },
      },
    })
    history.replace({ pathname: `/self-checkout/select-reservation` })
  }

  const subscribeReadResult = (processId: string) => {
    const intervalId = setInterval(async () => {
      try {
        const res = await api.getSelfCheckinCardReader(state.hotelId, serialNumber, pcId, processId)
        if (!res?.length) {
          return
        }
        if (!res[0].is_success) {
          clearInterval(intervalId)
          alert(t('cardIssuerConnectionError'))
          console.error('カード発行機に接続できませんでした')
          history.goBack()
          return
        }
        const completeMessage = res.find(r => r.command === CardReaderCommand.CardReadComplete)
        if (completeMessage) {
          clearInterval(intervalId)
          searchReservationByReader(completeMessage, processId)
        }
      } catch (e) {
        clearInterval(intervalId)
        errorHandler(e)
      }
    }, 500)
    return intervalId
  }

  useEffect(() => {
    if (initializedState && !state.searchReservation) {
      saveState(initializedState)
    }
  }, [initializedState])

  useEffect(() => {
    if (!processId) {
      return
    }
    const intervalId = subscribeReadResult(processId)
    return () => clearInterval(intervalId)
  }, [processId])

  if (!isInitialized) {
    return <></>
  }

  return (
    <FrontTabletLayout state={state}>
      <Header goToPreviousPage={history.goBack} title={t('Search your reservation')} />
      <Body>
        <div css={backgroundStyle}>
          <div css={componentStyle}>
            <div className="announce-block">
              <div className="issue-announcement-text">
                <Trans i18nKey={'TabletCheckOut.placeRoomKey'} components={{ span: <span /> }} />
              </div>
            </div>
            <div className="card-reader-image-block">
              <img src={require('@/static/images/self-checkin/checkin-card-reader-a.png')} />
              <img src={require('@/static/images/self-checkin/checkin-card-reader-b.png')} />
            </div>
          </div>
        </div>
        {isShowReadErrorDialog && (
          <ReadRoomKeyAnnounceModal
            onClose={() => {
              setIsShowReadErrorDialog(false)
              history.goBack()
            }}
          >
            <Trans i18nKey={'TabletCheckOut.roomKeyReadError'} />
          </ReadRoomKeyAnnounceModal>
        )}
        {isShowNotFoundErrorDialog && (
          <ReadRoomKeyAnnounceModal
            onClose={() => {
              setIsShowNotFoundErrorDialog(false)
              history.goBack()
            }}
          >
            <Trans i18nKey={'TabletCheckOut.reservationNotFound'} />
          </ReadRoomKeyAnnounceModal>
        )}
      </Body>
    </FrontTabletLayout>
  )
}

const backgroundStyle = css({
  background: 'white',
  border: '1px solid #F2F2F2',
  borderRadius: 10,
  padding: 40,
})
const componentStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: '100%',
  gap: 67,
  boxSizing: 'border-box',
  '.announce-block': {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '.issue-announcement-text': {
      marginTop: 8,
      fontSize: 24,
      fontWeight: 'bold',
      letterSpacing: 3.15,
      color: '#272727',
      lineHeight: 1.5,
      textAlign: 'center',
      wordBreak: 'keep-all',
      overflowWrap: 'anywhere',
      span: { color: '#F2A40B' },
    },
  },
  '.card-reader-image-block': {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    width: 374,
    maxWidth: 500,
    aspectRatio: '714 / 854',
    position: 'relative',
    '> img': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      opacity: 0,
      animation: 'image-switch-animation 10s infinite',
      '&:nth-of-type(1)': { animationDelay: '0s' },
      '&:nth-of-type(2)': { animationDelay: '5s' },
    },
  },
  '@keyframes image-switch-animation': {
    '0%': { opacity: 0 },
    '5%': { opacity: 1 },
    '60%': { opacity: 1 },
    '65%': { opacity: 0 },
  },
})
